import { useCallback, useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { CatalogItemRow } from "./catalogItems.types";

function pruneSelectionToPage({
    selection,
    pageRowsById,
}: {
    selection: RowSelectionState;
    pageRowsById: Map<string, CatalogItemRow>;
}): RowSelectionState {
    const pruned = Object.fromEntries(
        Object.entries(selection).filter(
            ([id, isSelected]) => isSelected && pageRowsById.has(id),
        ),
    );
    return Object.keys(pruned).length === Object.keys(selection).length
        ? selection
        : pruned;
}

export function useCatalogItemRowSelection(rows: CatalogItemRow[]) {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const pageRowsById = useMemo(
        () => new Map(rows.map((row) => [row.id, row])),
        [rows],
    );

    useEffect(() => {
        setRowSelection((selection) =>
            pruneSelectionToPage({ selection, pageRowsById }),
        );
    }, [pageRowsById]);

    const selectedRows = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([, isSelected]) => isSelected)
                .map(([id]) => pageRowsById.get(id))
                .filter((row): row is CatalogItemRow => !!row),
        [rowSelection, pageRowsById],
    );

    const selectOnly = useCallback((ids: string[]) => {
        setRowSelection(Object.fromEntries(ids.map((id) => [id, true])));
    }, []);

    return { rowSelection, setRowSelection, selectedRows, selectOnly };
}
